import type { CompositionV3Ent, PayV3Req } from '@revelio/data-access';

import type { Filter, PrimaryFilterId } from '../../types/filters';
import {
  getCompositionV3Corrections,
  getCompositionV3Filters,
} from '../composition-v3/getCompositionV3Filters';
import { normalizeCompositionEffectiveFilters } from '../composition-v3/getCompositionV3Variables';
import { getV3Entities } from '../get-v3-entities';
import { getV3Taxonomies } from '../get-v3-taxonomies';
import { stripEntitiesFromFilters } from '../role-skill-ent-kind';

/**
 * Assembles `pay_v3` request variables from effective filters, reusing the
 * composition-v3 encoding pipeline — `pay_v3` takes `composition_v3`'s request
 * types verbatim, minus `breakdown_options`. The pay-only `options` block
 * (top-N lengths, `geo_breakdown_state_over_msa`) is added by callers,
 * mirroring how the v2 providers layer `PayV2Options` onto `getV2Filters`.
 */
export const getPayV3Variables = ({
  primaryDimension,
  effectiveFilters,
  isSnapshot = false,
  entitiesOverride,
}: {
  primaryDimension: PrimaryFilterId;
  effectiveFilters: Filter[];
  isSnapshot?: boolean;
  /** When set, replaces the primary-dimension entities (e.g. chart overrides). */
  entitiesOverride?: CompositionV3Ent[];
}): PayV3Req => {
  const filtersToEncode =
    normalizeCompositionEffectiveFilters(effectiveFilters);

  const entities =
    entitiesOverride ??
    getV3Entities({
      primaryDimension,
      filters: filtersToEncode,
    });

  const filters = stripEntitiesFromFilters(
    getCompositionV3Filters({
      primaryDimension,
      isSnapshot,
      effectiveFilters: filtersToEncode,
    }),
    entities
  );

  const corrections = getCompositionV3Corrections(filtersToEncode);
  const { role_taxonomy, skill_taxonomy, activity_taxonomy } =
    getV3Taxonomies(filtersToEncode);

  return {
    entities,
    filters,
    corrections,
    role_taxonomy,
    skill_taxonomy,
    activity_taxonomy,
  };
};
